import { useEffect, useMemo, useRef } from 'react';
import { rotuloHumano } from '@/features/feicao/apresentacao';
import { useI18n } from '@/i18n/I18nProvider';
import { melhorAtributoDeRotulo } from '@/features/feicao/apresentacao';
import { useSelecao } from '@/features/feicao/selecao';
import { useCamadas } from '@/map/camadas';
import { ATRIBUICAO_DO_RELEVO, ID_DA_CAMADA_DE_RELEVO, ID_DA_CAMADA_HIPSOMETRICA, ID_DA_FONTE_DE_OPERACAO, ID_DA_FONTE_DE_RELEVO, ID_DA_FONTE_DE_ROTULO_DA_SELECAO, ID_DA_FONTE_DE_SELECAO, ID_DO_PREENCHIMENTO_DE_OPERACAO, URL_DA_FONTE_DE_RELEVO, aplicarEstiloNaCamada, camadaDaFonte, especificacaoDaFonte, especificacaoDaHipsometria, especificacaoDoRelevo, especificacoesDaOperacao, especificacoesDaSelecao, especificacaoDoRotuloDaCamada, especificacaoDoRotuloDaSelecao, filtroDeSelecaoDaCamada, filtroDoRotuloDaCamada, idDoRotuloDaCamada, especificacoesDasLayers, familiaDaGeometria, idDaFonte, idDoContorno, idDoPonto, idDoPreenchimento, idsDeLayersDaCamada, } from '@/map/layers';
import { useMapa } from '@/map/Mapa';
import { obterPontoDoRotulo } from '@/map/posicionamentoRotulo';
const COLECAO_VAZIA = { type: 'FeatureCollection', features: [] };
const ESPACO_DO_ENQUADRAMENTO = 72;
const ZOOM_MAXIMO_DO_ENQUADRAMENTO = 14;
const TOLERANCIA_DO_PONTEIRO = 4;
/**
 * Id da feição cujo rótulo individual está visível (geometria e atributos já carregados) quando
 * ela pertence à camada informada; essa feição deixa de receber o rótulo automático.
 */
function idDaFeicaoComRotuloIndividual(selecionada, camadaId) {
    return selecionada !== null &&
        selecionada.camadaId === camadaId &&
        selecionada.geometria !== null &&
        selecionada.feicao !== null
        ? selecionada.id
        : null;
}
/** Nome vindo dos atributos da feição; `null` quando eles não oferecem um nome próprio. */
function nomeProprio(atributos, id) {
    const nome = rotuloHumano(atributos, id, () => '');
    return nome === '' ? null : nome;
}
/**
 * Sincroniza com o MapLibre as camadas descobertas em `/camadas` e ativadas pelo usuário.
 * Toda camada é uma fonte vetorial MVT comum; o estilo depende só da família geométrica.
 */
export function CamadasNoMapa({ aoClicarNaFeicao, aoEncontrarSobreposicao, aoLimparSobreposicao, medicaoAtiva = false, }) {
    const mapa = useMapa();
    const { catalogo, ativas, definirCarga, operacao, enquadramento, relevo, selecaoPorCamada, estilos, rotulosPorCamada } = useCamadas();
    const { selecionada } = useSelecao();
    // Refs refletem os valores mais recentes sem entrar nas dependências dos efeitos abaixo.
    // Isso é o que evita o loop: `useChamada` muda `executar` a cada render; sem refs o efeito
    // do catálogo refazia, e estados de `selecaoPorCamada` não causam refetch em si mesmos.
    const estadoRef = useRef({
        catalogo,
        ativas,
        relevo,
        selecaoPorCamada,
        estilos,
        rotulosPorCamada,
        selecionada,
    });
    estadoRef.current = {
        catalogo,
        ativas,
        relevo,
        selecaoPorCamada,
        estilos,
        rotulosPorCamada,
        selecionada,
    };
    const idRotuloIndividual = idDaFeicaoComRotuloIndividual(selecionada, selecionada?.camadaId ?? '');
    // Texto de fallback do rótulo, lido no momento do uso: trocar o idioma não refaz camadas nem fontes.
    const { t, idioma } = useI18n();
    const rotuloPadraoRef = useRef((id) => t('feicao.rotuloPadrao', { id: String(id) }));
    rotuloPadraoRef.current = (id) => t('feicao.rotuloPadrao', { id: String(id) });
    const clicar = useRef(aoClicarNaFeicao);
    clicar.current = aoClicarNaFeicao;
    const encontrarSobreposicao = useRef(aoEncontrarSobreposicao);
    encontrarSobreposicao.current = aoEncontrarSobreposicao;
    const limparSobreposicao = useRef(aoLimparSobreposicao);
    limparSobreposicao.current = aoLimparSobreposicao;
    // Lida pelos handlers do mapa: vale o valor mais recente, sem depender da ordem dos listeners.
    const medindo = useRef(medicaoAtiva);
    medindo.current = medicaoAtiva;
    // Ao iniciar a medição, fecha o seletor contextual que estiver aberto.
    useEffect(() => {
        if (medicaoAtiva) {
            limparSobreposicao.current?.();
        }
    }, [medicaoAtiva]);
    // Ativas × catálogo: derivado puro e estável em identidade quando nada muda. É dep de efeito
    // para reagir a ativações/desativações; também fica no ref para uso síncrono por outros efeitos.
    const desenhaveis = useMemo(() => {
        const porId = new Map((catalogo ?? []).map((camada) => [camada.id, camada]));
        return ativas.flatMap((id) => {
            const camada = porId.get(id);
            const familia = camada === undefined ? null : familiaDaGeometria(camada.tipo_geometria);
            return camada === undefined || familia === null ? [] : [{ id, familia }];
        });
    }, [catalogo, ativas]);
    const desenhaveisRef = useRef(desenhaveis);
    desenhaveisRef.current = desenhaveis;
    // Fontes e layers base: criadas uma única vez por instância do mapa.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        if (mapa.getSource(ID_DA_FONTE_DE_RELEVO) === undefined) {
            mapa.addSource(ID_DA_FONTE_DE_RELEVO, {
                type: 'raster-dem',
                tiles: [URL_DA_FONTE_DE_RELEVO],
                tileSize: 256,
                encoding: 'terrarium',
                maxzoom: 15,
                attribution: ATRIBUICAO_DO_RELEVO,
            });
            // Hipsometria antes do hillshade: a cor por altitude fica abaixo do sombreamento.
            if (mapa.getLayer(ID_DA_CAMADA_HIPSOMETRICA) === undefined) {
                mapa.addLayer(especificacaoDaHipsometria());
            }
            mapa.addLayer(especificacaoDoRelevo());
            // O provider é a fonte única da intensidade e da visibilidade: o padrão da
            // especificação não pode sobrescrevê-las.
            const { relevo: relevoAtual } = estadoRef.current;
            mapa.setPaintProperty(ID_DA_CAMADA_DE_RELEVO, 'hillshade-exaggeration', relevoAtual.hillshadeIntensidade);
            mapa.setLayoutProperty(ID_DA_CAMADA_DE_RELEVO, 'visibility', relevoAtual.hillshadeAtivo ? 'visible' : 'none');
        }
        if (mapa.getSource(ID_DA_FONTE_DE_OPERACAO) === undefined) {
            mapa.addSource(ID_DA_FONTE_DE_OPERACAO, { type: 'geojson', data: COLECAO_VAZIA });
            for (const layer of especificacoesDaOperacao()) {
                mapa.addLayer(layer);
            }
        }
        if (mapa.getSource(ID_DA_FONTE_DE_SELECAO) === undefined) {
            mapa.addSource(ID_DA_FONTE_DE_SELECAO, { type: 'geojson', data: COLECAO_VAZIA });
            for (const layer of especificacoesDaSelecao()) {
                mapa.addLayer(layer);
            }
        }
        if (mapa.getSource(ID_DA_FONTE_DE_ROTULO_DA_SELECAO) === undefined) {
            mapa.addSource(ID_DA_FONTE_DE_ROTULO_DA_SELECAO, { type: 'geojson', data: COLECAO_VAZIA });
            mapa.addLayer(especificacaoDoRotuloDaSelecao());
        }
    }, [mapa]);
    // Ativar uma camada cria a fonte MVT; desativar remove layers e fonte. Dependências
    // estáveis: identidade de `mapa` e o conjunto `desenhaveis` (catalogo/eferente).
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const { catalogo: c } = estadoRef.current;
        const ativos = desenhaveisRef.current;
        const desejadas = new Set(ativos.map(({ id }) => id));
        for (const camada of c ?? []) {
            if (desejadas.has(camada.id)) {
                continue;
            }
            for (const id of idsDeLayersDaCamada(camada.id)) {
                if (mapa.getLayer(id) !== undefined) {
                    mapa.removeLayer(id);
                }
            }
            if (mapa.getSource(idDaFonte(camada.id)) !== undefined) {
                mapa.removeSource(idDaFonte(camada.id));
            }
        }
        for (const { id, familia } of ativos) {
            if (mapa.getSource(idDaFonte(id)) !== undefined) {
                continue;
            }
            if (familia === null) {
                continue;
            }
            definirCarga(id, { estado: 'carregando' });
            mapa.addSource(idDaFonte(id), especificacaoDaFonte(id));
            // O estilo inicial é o default da família; mudanças subsequentes chegam via `estilos`.
            for (const layer of especificacoesDasLayers(id, familia)) {
                mapa.addLayer(layer, ID_DO_PREENCHIMENTO_DE_OPERACAO);
            }
            // Rótulos automáticos: só com atributo nominal publicado. Visibilidade e filtro já nascem
            // com os valores atuais, para não exibir texto fora da preferência ou da seleção.
            const camada = c?.find((item) => item.id === id);
            const atributo = camada === undefined ? null : melhorAtributoDeRotulo(camada.atributos_mvt);
            if (atributo !== null) {
                mapa.addLayer(especificacaoDoRotuloDaCamada(id, familia, atributo, filtroDoRotuloDaCamada(atributo, filtroDeSelecaoDaCamada(estadoRef.current.selecaoPorCamada[id] ?? { todos: true, ids: [] }), idDaFeicaoComRotuloIndividual(estadoRef.current.selecionada, id)), estadoRef.current.rotulosPorCamada[id] ?? true), ID_DO_PREENCHIMENTO_DE_OPERACAO);
            }
        }
    }, [mapa, desenhaveis, definirCarga]);
    // Ordem visual: `ativas` vai da base ao topo. Cada camada é movida (com suas layers, na ordem
    // interna) para logo abaixo da operação; a última movida fica no topo das camadas MVT. Somente
    // `moveLayer`: nenhuma fonte ou layer é recriada.
    useEffect(() => {
        if (mapa === null || mapa.getLayer(ID_DO_PREENCHIMENTO_DE_OPERACAO) === undefined) {
            return;
        }
        for (const { id } of desenhaveis) {
            for (const layerId of idsDeLayersDaCamada(id)) {
                if (mapa.getLayer(layerId) !== undefined) {
                    mapa.moveLayer(layerId, ID_DO_PREENCHIMENTO_DE_OPERACAO);
                }
            }
        }
    }, [mapa, desenhaveis]);
    // Reaplica o estilo atual sempre que o objeto de estilos muda (não em todo render).
    // Lê estilos direto do ref para não entrar nas deps do efeito.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const estilos = estadoRef.current.estilos;
        for (const { id } of desenhaveisRef.current) {
            if (mapa.getSource(idDaFonte(id)) === undefined) {
                continue;
            }
            const estilo = estilos[id] ?? null;
            if (estilo === null) {
                continue;
            }
            aplicarEstiloNaCamada(mapa, id, estilo);
        }
    }, [mapa, estilos]);
    // Hillshade: liga/desliga e ajusta intensidade sem recriar a layer.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        if (mapa.getLayer(ID_DA_CAMADA_DE_RELEVO) !== undefined) {
            mapa.setLayoutProperty(ID_DA_CAMADA_DE_RELEVO, 'visibility', relevo.hillshadeAtivo ? 'visible' : 'none');
            mapa.setPaintProperty(ID_DA_CAMADA_DE_RELEVO, 'hillshade-exaggeration', relevo.hillshadeIntensidade);
        }
    }, [mapa, relevo.hillshadeAtivo, relevo.hillshadeIntensidade]);
    // Carga e erro são observados por camada.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const aoCarregar = (evento) => {
            const id = evento.sourceId === undefined ? null : camadaDaFonte(evento.sourceId);
            if (id !== null) {
                definirCarga(id, { estado: 'carregando' });
            }
        };
        const aoReceber = (evento) => {
            const id = evento.sourceId === undefined ? null : camadaDaFonte(evento.sourceId);
            if (id !== null && evento.isSourceLoaded === true) {
                definirCarga(id, { estado: 'pronta' });
            }
        };
        const aoFalhar = (evento) => {
            const id = evento.sourceId === undefined ? null : camadaDaFonte(evento.sourceId);
            if (id !== null) {
                // Sem mensagem do MapLibre, a interface exibe o texto genérico traduzido.
                const mensagem = evento.error?.message;
                definirCarga(id, mensagem === undefined ? { estado: 'erro' } : { estado: 'erro', mensagem });
            }
        };
        mapa.on('dataloading', aoCarregar);
        mapa.on('sourcedata', aoReceber);
        mapa.on('error', aoFalhar);
        return () => {
            mapa.off('dataloading', aoCarregar);
            mapa.off('sourcedata', aoReceber);
            mapa.off('error', aoFalhar);
        };
    }, [mapa, definirCarga]);
    // Destaque da feição selecionada por clique no mapa.
    const geometriaSelecionada = selecionada?.geometria?.geometria;
    const atributosSelecionados = selecionada?.feicao?.atributos;
    const idSelecionado = selecionada?.id;
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const colecao = geometriaSelecionada === undefined
            ? COLECAO_VAZIA
            : {
                type: 'FeatureCollection',
                features: [{ type: 'Feature', geometry: geometriaSelecionada, properties: {} }],
            };
        mapa.getSource(ID_DA_FONTE_DE_SELECAO)?.setData(colecao);
    }, [mapa, geometriaSelecionada]);
    // Rótulo da seleção: um único ponto, só quando geometria e atributos já chegaram (sem nome
    // provisório baseado no ID). Seleção limpa ou incompleta esvazia a fonte.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const ponto = geometriaSelecionada === undefined ? null : obterPontoDoRotulo(geometriaSelecionada);
        const colecao = ponto === null || atributosSelecionados === undefined || idSelecionado === undefined
            ? COLECAO_VAZIA
            : {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: ponto },
                        properties: { rotulo: rotuloHumano(atributosSelecionados, idSelecionado, rotuloPadraoRef.current) },
                    },
                ],
            };
        mapa.getSource(ID_DA_FONTE_DE_ROTULO_DA_SELECAO)?.setData(colecao);
        // `idioma`: o rótulo genérico acompanha a troca de idioma (apenas `setData`, sem refazer camadas).
    }, [mapa, geometriaSelecionada, atributosSelecionados, idSelecionado, idioma]);
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        mapa.getSource(ID_DA_FONTE_DE_OPERACAO)?.setData(operacao ?? COLECAO_VAZIA);
    }, [mapa, operacao]);
    // Realce da seleção múltipla do painel: o componente `SeletorDeFeicoes` se encarrega
    // de alimentar a fonte `ID_DA_FONTE_DE_SELECAO_MULTIPLA` por camada ao marcar/desmarcar.
    // "Todos" / seleção individual: aplica filtro por id diretamente nas layers MVT.
    // - Todos=true  => sem filtro (todas as feições do tile visíveis).
    // - Todos=false + ids => somente as feições cujo `id` está em `ids`.
    // - Todos=false + vazio => nenhuma feição da camada visível.
    // Nenhuma geometria é baixada; nenhum GeoJSON paralelo é criado.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        const { catalogo: c, selecaoPorCamada: porCamada, selecionada: atual } = estadoRef.current;
        for (const { id } of desenhaveisRef.current) {
            const filtroDeSelecao = filtroDeSelecaoDaCamada(porCamada[id] ?? { todos: true, ids: [] });
            const idsGeometricos = [idDoPreenchimento(id), idDoContorno(id), idDoPonto(id)];
            for (const layerId of idsGeometricos) {
                if (mapa.getLayer(layerId) !== undefined) {
                    mapa.setFilter(layerId, filtroDeSelecao);
                }
            }
            // Rótulos: mesmo filtro de seleção, sem o texto da feição que tem rótulo individual.
            const camada = c?.find((item) => item.id === id);
            const atributo = camada === undefined ? null : melhorAtributoDeRotulo(camada.atributos_mvt);
            if (atributo !== null && mapa.getLayer(idDoRotuloDaCamada(id)) !== undefined) {
                mapa.setFilter(idDoRotuloDaCamada(id), filtroDoRotuloDaCamada(atributo, filtroDeSelecao, idDaFeicaoComRotuloIndividual(atual, id)));
            }
        }
    }, [mapa, selecaoPorCamada, desenhaveis, idRotuloIndividual, selecionada?.camadaId]);
    // Visibilidade dos rótulos automáticos: alterna sem recriar fonte nem layer.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        for (const { id } of desenhaveisRef.current) {
            if (mapa.getLayer(idDoRotuloDaCamada(id)) !== undefined) {
                mapa.setLayoutProperty(idDoRotuloDaCamada(id), 'visibility', (rotulosPorCamada[id] ?? true) ? 'visible' : 'none');
            }
        }
    }, [mapa, rotulosPorCamada, desenhaveis]);
    useEffect(() => {
        if (mapa === null || enquadramento === null) {
            return;
        }
        mapa.fitBounds(enquadramento.limites, {
            padding: ESPACO_DO_ENQUADRAMENTO,
            maxZoom: ZOOM_MAXIMO_DO_ENQUADRAMENTO,
            duration: 600,
        });
    }, [mapa, enquadramento]);
    // Interação: o id da feição vem do próprio tile MVT.
    useEffect(() => {
        if (mapa === null) {
            return;
        }
        // Feições renderizadas sob o ponteiro, só das layers MVT do Viewer. O MapLibre já exclui
        // layers com `visibility: none` e feições removidas por filtro, na ordem de desenho
        // (topo primeiro).
        const consultar = (evento) => {
            const layers = desenhaveisRef.current
                .flatMap(({ id }) => idsDeLayersDaCamada(id))
                .filter((id) => mapa.getLayer(id) !== undefined);
            if (layers.length === 0) {
                return [];
            }
            const { x, y } = evento.point;
            return mapa.queryRenderedFeatures([
                [x - TOLERANCIA_DO_PONTEIRO, y - TOLERANCIA_DO_PONTEIRO],
                [x + TOLERANCIA_DO_PONTEIRO, y + TOLERANCIA_DO_PONTEIRO],
            ], { layers });
        };
        // Deduplica por `camadaId:feicaoId` (preenchimento, contorno, rótulo, fragmentos de tile e
        // partes de multigeometrias colapsam em uma entrada) e ordena pela prioridade visual:
        // camada superior de `ativas` primeiro; dentro da camada, a ordem do MapLibre (sort estável).
        const candidatasDe = (feicoes) => {
            const { catalogo: c, ativas: ordemAtiva } = estadoRef.current;
            const vistas = new Set();
            const candidatas = [];
            for (const feicao of feicoes) {
                const camadaId = camadaDaFonte(feicao.source);
                if (camadaId === null || typeof feicao.id !== 'number' || !Number.isFinite(feicao.id)) {
                    continue;
                }
                const chave = `${camadaId}:${feicao.id}`;
                if (vistas.has(chave)) {
                    continue;
                }
                vistas.add(chave);
                candidatas.push({
                    camadaId,
                    feicaoId: feicao.id,
                    nome: nomeProprio(feicao.properties, feicao.id),
                    nomeDaCamada: c?.find((item) => item.id === camadaId)?.nome ?? camadaId,
                });
            }
            return candidatas
                .map((candidata, indice) => ({ candidata, indice }))
                .sort((a, b) => ordemAtiva.indexOf(b.candidata.camadaId) - ordemAtiva.indexOf(a.candidata.camadaId) ||
                a.indice - b.indice)
                .map(({ candidata }) => candidata);
        };
        const aoClicar = (evento) => {
            if (medindo.current) {
                return;
            }
            const candidatas = candidatasDe(consultar(evento));
            if (candidatas.length === 0) {
                limparSobreposicao.current?.();
                return;
            }
            const [unica] = candidatas;
            if (unica !== undefined && candidatas.length === 1) {
                limparSobreposicao.current?.();
                clicar.current?.(unica.camadaId, unica.feicaoId);
                return;
            }
            encontrarSobreposicao.current?.({
                x: evento.point.x,
                y: evento.point.y,
                candidatas,
            });
        };
        const aoMover = (evento) => {
            if (medindo.current) {
                return;
            }
            mapa.getCanvas().style.cursor =
                consultar(evento).some((feicao) => typeof feicao.id === 'number') ? 'pointer' : '';
        };
        // Qualquer pan, zoom ou enquadramento invalida a posição do seletor.
        const aoMovimentar = () => {
            limparSobreposicao.current?.();
        };
        mapa.on('click', aoClicar);
        mapa.on('mousemove', aoMover);
        mapa.on('movestart', aoMovimentar);
        return () => {
            mapa.off('click', aoClicar);
            mapa.off('mousemove', aoMover);
            mapa.off('movestart', aoMovimentar);
        };
    }, [mapa]);
    return null;
}
